import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import {
  Choices,
  CompanyPicker,
  CountryField,
  DocumentsToProvide,
  FORMALITY_COUNTRIES,
  FormalityPage,
  TextField,
  fieldError,
} from "../../../components/formalities/FormalityParts";
import { showError } from "../../../components/options/OptionParts";
import { addToCart } from "../../../redux/actions/CartActions";
import { createTaxRegistrationRequest } from "../../../redux/actions/FormalityActions";

const TAX_REGISTRATION_PRICE = 5000;

const APPLICANT_FIELDS = [
  { name: "address", label: "Adresse", placeholder: "Quartier, ville" },
  { name: "phone", label: "Téléphone", type: "tel" },
  { name: "email", label: "Adresse e-mail", type: "email" },
  { name: "profession", label: "Profession", placeholder: "Ex. : Commerçant" },
];

const COMPANY_FIELDS = [
  { name: "businessName", label: "Nom de l'entreprise" },
  { name: "companyPhone", label: "Téléphone de l'entreprise", type: "tel" },
  {
    name: "companyEmail",
    label: "Adresse e-mail de l'entreprise",
    type: "email",
  },
];

// Taxpayer registration certificate (NIU): an existing number, or a new
// registration of a person or a company
function TaxRegistration() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.authUser);
  const { userCompanies } = useSelector((state) => state.companies);

  const [country, setCountry] = useState(FORMALITY_COUNTRIES[0]);
  const [alreadyRegistered, setAlreadyRegistered] = useState(null);
  const [holderType, setHolderType] = useState("person");
  const [values, setValues] = useState({
    niu: "",
    address: "",
    phone: user?.mobile || "",
    email: user?.email || "",
    profession: user?.profession || "",
    businessName: "",
    companyPhone: "",
    companyEmail: "",
  });
  const [touched, setTouched] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const set = (name) => (value) =>
    setValues((previous) => ({ ...previous, [name]: value }));

  const forCompany = holderType === "company";
  const answered = alreadyRegistered !== null;

  // Fields asked for the chosen situation
  const fields = !answered
    ? []
    : alreadyRegistered
      ? [
          {
            name: "niu",
            label: "Numéro d'identifiant unique (NIU)",
            placeholder: "Ex. : P012345678901A",
          },
        ]
      : [...APPLICANT_FIELDS, ...(forCompany ? COMPANY_FIELDS : [])];
  const hasDocuments = answered && !alreadyRegistered;
  const isValid =
    country &&
    answered &&
    fields.every((field) => !fieldError(values[field.name], field.type));

  const handleCreated = async ({ success, message }) => {
    if (!success) {
      setSubmitting(false);
      showError(Swal, message);
      return;
    }

    await dispatch(
      addToCart({ from: "taxRegistration", price: TAX_REGISTRATION_PRICE }),
    );
    setSubmitting(false);
    navigate(hasDocuments ? "/formality-documents" : "/formality-payment");
  };

  const handleContinue = () => {
    setTouched(true);
    if (!isValid) return;

    setSubmitting(true);
    dispatch(
      createTaxRegistrationRequest(
        user.token,
        {
          country,
          alreadyRegistered,
          ...(alreadyRegistered ? {} : { holderType }),
          ...Object.fromEntries(
            fields.map((field) => [field.name, values[field.name].trim()]),
          ),
        },
        handleCreated,
      ),
    );
  };

  return (
    <FormalityPage
      from="taxRegistration"
      title="Attestation d'immatriculation (NIU)"
      lead="Pour une personne physique ou une entreprise, déjà immatriculée ou non."
      price={TAX_REGISTRATION_PRICE}
      submitting={submitting}
      onContinue={handleContinue}
    >
      <CountryField value={country} onChange={setCountry} />

      <Choices
        label="Avez-vous déjà un numéro d'identifiant unique (NIU) ?"
        options={[
          { value: true, label: "Oui, j'ai déjà un NIU" },
          { value: false, label: "Non, pas encore" },
        ]}
        value={alreadyRegistered}
        onChange={setAlreadyRegistered}
        error={touched && !answered ? "Choisissez une réponse." : null}
      />

      {hasDocuments && (
        <Choices
          label="Pour qui est la demande ?"
          options={[
            { value: "person", label: "Personne physique" },
            { value: "company", label: "Entreprise" },
          ]}
          value={holderType}
          onChange={setHolderType}
        />
      )}

      {hasDocuments && forCompany && (
        <CompanyPicker
          companies={userCompanies}
          onPick={(company) =>
            company &&
            setValues((previous) => ({
              ...previous,
              businessName: company.businessName || "",
              companyPhone: company.mobile || previous.companyPhone,
              companyEmail: company.email || previous.companyEmail,
            }))
          }
        />
      )}

      {fields.map((field) => (
        <TextField
          key={field.name}
          id={`tax-registration-${field.name}`}
          label={field.label}
          type={field.type}
          placeholder={field.placeholder}
          value={values[field.name]}
          onChange={set(field.name)}
          error={touched ? fieldError(values[field.name], field.type) : null}
        />
      ))}

      {hasDocuments && (
        <DocumentsToProvide
          documents={[
            "Pièce d'identité",
            ...(forCompany ? ["Registre de commerce"] : []),
          ]}
        />
      )}
    </FormalityPage>
  );
}

export default TaxRegistration;
